import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import { TZUtils } from 'lib/tzutils';

import { Comment } from 'data/models/Comment';
import { Event } from 'data/models/Event';
import { TaggedCommentUser } from 'data/models/TaggedCommentUser';

import { PCCache } from 'data/PCCache';

import { NewComment, NewCommentParams } from './newcomment';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';

// TH 02/16/2017 -- The Event side of any of our Relevance tables.
// For this component, we don't care what the Relevance joins to.
export interface Relevance {
    EventId: () => number;
    Event: () => Event | null;
}

export interface EventLogParams {
    log : ArraySignal<Relevance>;
    canComment : (entry : Relevance) => boolean;
}

// TH 02/23/2017 -- EventLog handles displaying a list of Relevances, w/ an option to comment on individual entries.
// But doesn't handle adding a new top-level Comment (use NewComment) or polling to update the Log (use LogUpdater).
export type EventLog = ReturnType<typeof EventLog>;
export function EventLog(
    tzutils : TZUtils,
    cache : PCCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    newCommentModel : (params : NewCommentParams) => NewComment,
    { log, canComment } : EventLogParams
) {
    // Mini DI kernel...
    const
        recipientModel = (taggedUser : TaggedCommentUser) => CommentRecipient(taggedUser, cache, personProfilePopupModel),
        commentModel = (comment : Comment) => LogEntryComment(comment, recipientModel),
        logEntryModel = (entry : Relevance) => LogEntry(entry, () => canComment(entry), newCommentModel, commentModel);

    const
        LogEntries = log
            .orderBy(entry => entry.Event()?.Date() ?? null)
            .reverse()
            .map(logEntryModel),
        clock = tzutils.clock(1000),
        Now = () => clock.now();

    return { LogEntries, Now };
}

export type LogEntry = ReturnType<typeof LogEntry>;
function LogEntry(
    Entry : Relevance,
    CanComment: () => boolean,
    newCommentModel : (params : NewCommentParams) => NewComment,
    commentModel : (comment : Comment) => LogEntryComment
) {
    const
        Comment = S(() => Entry.Event()?.Comment() ? commentModel(Entry.Event()!.Comment()!) : null),
        Comments = lift(() => Entry.Event()?.Comments() ?? [])
            .orderBy(c => c.Event()?.Date() ?? null)
            .reverse()
            .map(commentModel),
        showNewComment = S.value(false),
        NewComment = S(() => {
            if (showNewComment()) {
                const newComment = newCommentModel({
                    url: () => Entry.Event()?.CommentsUrl() ?? ''
                });
                // Hide the new comment view after posting a comment, which we detect by
                // listening for the newComment model resetting to a fresh comment draft.
                S.on(newComment.NewComment, () => showNewComment(false), undefined, true);
                return newComment;
            } else {
                return null;
            }
        });

    return {
        Entry,
        Comment,
        Comments,
        CanComment,
        ToggleNewComment: () => showNewComment(!showNewComment()),
        NewComment
    };
}

export type LogEntryComment = ReturnType<typeof LogEntryComment>;
function LogEntryComment(
    Comment : Comment,
    recipientModel : (taggedUser : TaggedCommentUser) => CommentRecipient
) {
    const Recipients = Comment.TaggedUsers.map(recipientModel);
    return { Comment, Recipients };
}

export type CommentRecipient = ReturnType<typeof CommentRecipient>;
function CommentRecipient(
    taggedUser : TaggedCommentUser,
    cache : PCCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const
        User = cache.PCUsers.update1(null, { Id: taggedUser.UserId() }),
        showProfile = S.value(false),
        ProfilePopup = S(() => showProfile() ? personProfilePopupModel({
            id: taggedUser.UserId(),
            onHidePopup: () => showProfile(false)
        }) : null);

    return {
        User,
        ToggleProfile: () => showProfile(!showProfile()),
        ProfilePopup
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/eventlog/model.ts